import React, { useEffect, useState, useRef } from "react";
import service from "../../Service/Service";
import { filterSanctionedAirports } from "../../constants/sanctionedCountries";
import MyInput from "../Input/MyInput";
import { ThumbsUp } from "react-feather";
import { useLocation, useNavigate } from "react-router-dom";
import './input.css'
import Spinner from "../../assets/images/icons/spinner2.gif";
import { PhoneCall, PlaneTakeoff, PlaneLanding, ArrowUpDown, Calendar, X } from "lucide-react";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import Select from './SelectAsync'

// Mobile Calendar Components
const CalendarMonth = ({ year, month, selectedDate, onSelectDate, minDate, returnDate, departureDate, isSelectingReturn }) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  
  const days = [];
  
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="h-12"></div>);
  }
  
  // Add cells for each day of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const currentDate = new Date(year, month, day);
    currentDate.setHours(0, 0, 0, 0);
    
    const isPast = currentDate < (minDate || today);
    const isSelected = selectedDate && 
      currentDate.getFullYear() === selectedDate.getFullYear() &&
      currentDate.getMonth() === selectedDate.getMonth() &&
      currentDate.getDate() === selectedDate.getDate();
    
    const isDeparture = departureDate && 
      currentDate.getFullYear() === departureDate.getFullYear() &&
      currentDate.getMonth() === departureDate.getMonth() &&
      currentDate.getDate() === departureDate.getDate();
    
    const isReturn = returnDate && 
      currentDate.getFullYear() === returnDate.getFullYear() &&
      currentDate.getMonth() === returnDate.getMonth() &&
      currentDate.getDate() === returnDate.getDate();
    
    const isInRange = departureDate && returnDate && 
      currentDate > departureDate && currentDate < returnDate;
    
    days.push(
      <button
        key={day}
        type="button"
        onClick={() => !isPast && onSelectDate(currentDate)}
        disabled={isPast}
        className={`h-12 flex items-center justify-center text-sm font-medium transition-all
          ${isPast ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 cursor-pointer hover:bg-navy-50'}
          ${isSelected || isDeparture || isReturn ? 'bg-secondary text-white hover:bg-navy-700' : ''}
          ${isInRange ? 'bg-navy-100' : ''}
          rounded-lg
        `}
      >
        {day}
      </button>
    );
  }
  
  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">
        {monthNames[month]} {year}
      </h3>
      <div className="grid grid-cols-7 gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="h-8 flex items-center justify-center text-xs font-semibold text-gray-500">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {days}
      </div>
    </div>
  );
};

const VerticalCalendar = ({ selectedDate, onSelectDate, minDate, returnDate, departureDate, isSelectingReturn }) => {
  const today = new Date();
  const months = [];
  
  // Generate 12 months starting from current month
  for (let i = 0; i < 12; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
    months.push({
      year: date.getFullYear(),
      month: date.getMonth()
    });
  }
  
  return (
    <div className="overflow-y-auto max-h-[70vh] px-4 pb-4">
      {months.map((m, index) => (
        <CalendarMonth
          key={`${m.year}-${m.month}`}
          year={m.year}
          month={m.month}
          selectedDate={selectedDate}
          onSelectDate={onSelectDate}
          minDate={minDate}
          returnDate={returnDate}
          departureDate={departureDate}
          isSelectingReturn={isSelectingReturn}
        />
      ))}
    </div>
  );
};

const InputSection = ({ setShowToast, callby, initialValues, embeddedInOverlay, onSearch }) => {
  // Pre-fill values from initialValues if provided (for edit mode)
  const [departure, setDeparture] = useState(initialValues?.departure || '');
  const [arrival, setArrival] = useState(initialValues?.arrival || '');
  const [departure_name, setDepartureName] = useState(initialValues?.departure_name || '');
  const [arrival_name, setArrivalName] = useState(initialValues?.arrival_name || '');
  const [date, setDate] = useState(initialValues?.date || '');
  const [return_date, setReturnDate] = useState(initialValues?.return_date || '');
  const [name, setName] = useState(initialValues?.name || '');
  const [phone, setPhone] = useState(initialValues?.phone || '');
  const [email, setEmail] = useState(initialValues?.email || '');
  
  // Phone number validation function
  const isValidPhoneNumber = (phoneNumber) => {
    if (!phoneNumber || phoneNumber.trim() === '') return false;
    
    // Check minimum length of 10 digits
    if (phoneNumber.length < 10) return false;
    
    // Check if phone number starts with valid prefixes
    const validPrefixes = ['7', '07', '44', '02', '00'];
    return validPrefixes.some(prefix => phoneNumber.startsWith(prefix));
  };

  // Handle phone number input - only allow numbers
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    const numbersOnly = value.replace(/[^0-9]/g, '');
    setPhone(numbersOnly);
  };
  
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);

  const [toast, showToast] = useState(false)
  const [error, setError] = useState('')
  const [flight_type, setFlightType] = useState(initialValues?.return_date ? 'return' : (initialValues?.flight_type || 'one-way'))
  const [flight_class, setFlightClass] = useState(initialValues?.flight_class || 'economy')
  const [submitLoading, setSubmitLoading] = useState(false)
  
  // Mobile calendar states
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [calendarStep, setCalendarStep] = useState('departure'); // 'departure' or 'return'
  const [isMobile, setIsMobile] = useState(false);
  
  // Mobile passengers modal state
  const [showPassengersModal, setShowPassengersModal] = useState(false);
  
  // Mobile city selection modal state
  const [showCityModal, setShowCityModal] = useState(false);
  const [cityModalType, setCityModalType] = useState('departure'); // 'departure' or 'arrival'
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [citySearchResults, setCitySearchResults] = useState([]);
  const [citySearchLoading, setCitySearchLoading] = useState(false);

  // Popular cities list - Lahore, Karachi, Islamabad at end
  const popularCities = [
    { label: 'London (LHR)', value: 'LHR' },
    { label: 'Manchester (MAN)', value: 'MAN' },
    { label: 'Birmingham (BHX)', value: 'BHX' },
    { label: 'Edinburgh (EDI)', value: 'EDI' },
    { label: 'Glasgow (GLA)', value: 'GLA' },
    { label: 'Dubai (DXB)', value: 'DXB' },
    { label: 'New York (JFK)', value: 'JFK' },
    { label: 'Paris (CDG)', value: 'CDG' },
    { label: 'Amsterdam (AMS)', value: 'AMS' },
    { label: 'Frankfurt (FRA)', value: 'FRA' },
    { label: 'Istanbul (IST)', value: 'IST' },
    { label: 'Doha (DOH)', value: 'DOH' },
    { label: 'Toronto (YYZ)', value: 'YYZ' },
    { label: 'Sydney (SYD)', value: 'SYD' },
    { label: 'Singapore (SIN)', value: 'SIN' },
    { label: 'Bangkok (BKK)', value: 'BKK' },
    { label: 'Tokyo (NRT)', value: 'NRT' },
    { label: 'Lahore (LHE)', value: 'LHE' },
    { label: 'Karachi (KHI)', value: 'KHI' },
    { label: 'Islamabad (ISB)', value: 'ISB' }
  ];
  
  const location = useLocation();
  const navigate = useNavigate();

  const departureInputRef = useRef(null);  // DOM input element
  const returnInputRef = useRef(null);      // DOM input element
  const departureFpRef = useRef(null);      // flatpickr instance
  const returnFpRef = useRef(null);         // flatpickr instance

  // Restore form data from localStorage when component mounts
  useEffect(() => {
    // Only restore if no initialValues are provided (to avoid overriding edit mode)
    if (!initialValues) {
      try {
        const savedBooking = localStorage.getItem('booking');
        if (savedBooking) {
          const bookingData = JSON.parse(savedBooking);
          
          // Restore form fields from saved booking data
          if (bookingData.departure) setDeparture(bookingData.departure);
          if (bookingData.arrival) setArrival(bookingData.arrival);
          if (bookingData.departure_name) setDepartureName(bookingData.departure_name);
          if (bookingData.arrival_name) setArrivalName(bookingData.arrival_name);
          if (bookingData.date) setDate(bookingData.date);
          if (bookingData.return_date) {
            setReturnDate(bookingData.return_date);
          }
          if (bookingData.name) setName(bookingData.name);
          if (bookingData.phone) setPhone(bookingData.phone);
          if (bookingData.email) setEmail(bookingData.email);
          if (bookingData.adults) setAdults(parseInt(bookingData.adults) || 1);
          if (bookingData.children) setChildren(parseInt(bookingData.children) || 0);
          if (bookingData.infants) setInfants(parseInt(bookingData.infants) || 0);
          // Set flight type - use saved value or infer from return_date
          if (bookingData.flight_type) {
            setFlightType(bookingData.flight_type);
          } else if (bookingData.return_date) {
            setFlightType('return');
          } else {
            setFlightType('one-way');
          }
          if (bookingData.flight_class) setFlightClass(bookingData.flight_class);
          
          console.log('Form data restored from localStorage:', bookingData);
        }
      } catch (error) {
        console.error('Error restoring form data from localStorage:', error);
      }
    }
  }, []); // Run only once on mount

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize departure flatpickr imperatively on mount
  useEffect(() => {
    if (!departureInputRef.current) return;
    const fp = flatpickr(departureInputRef.current, {
      dateFormat: 'd M, Y',
      minDate: 'today',
      showMonths: 2,
      disableMobile: true,
      appendTo: document.body,
      onChange: (selectedDates) => {
        if (selectedDates[0]) {
          setDate(formatDateForStorage(selectedDates[0]));
        }
      },
    });
    departureFpRef.current = fp;
    return () => fp.destroy();
  }, []);

  // Initialize return flatpickr imperatively on mount
  useEffect(() => {
    if (!returnInputRef.current) return;
    const fp = flatpickr(returnInputRef.current, {
      dateFormat: 'd M, Y',
      minDate: 'today',
      showMonths: 2,
      disableMobile: true,
      appendTo: document.body,
      onChange: (selectedDates) => {
        if (selectedDates[0]) {
          setReturnDate(formatDateForStorage(selectedDates[0]));
        }
      },
    });
    returnFpRef.current = fp;
    return () => fp.destroy();
  }, []);

  // Sync departure date to flatpickr instance when changed externally (localStorage restore / initialValues)
  useEffect(() => {
    const fp = departureFpRef.current;
    if (!fp) return;
    if (fp.isOpen) return; // never interfere while open
    if (date) {
      fp.setDate(new Date(date), false);
    } else {
      fp.clear();
    }
  }, [date]);

  // Sync return date to flatpickr instance when changed externally
  useEffect(() => {
    const fp = returnFpRef.current;
    if (!fp) return;
    if (fp.isOpen) return;
    if (return_date) {
      fp.setDate(new Date(return_date), false);
    } else {
      fp.clear();
    }
  }, [return_date]);

  // Update return picker minDate when departure date changes
  // minDate equals departure date so same-day return is allowed but nothing before it
  useEffect(() => {
    const fp = returnFpRef.current;
    if (fp) {
      if (date) {
        fp.set('minDate', new Date(date));
        // If existing return_date is now invalid, clear it
        if (return_date && return_date < date) {
          setReturnDate('');
          fp.clear();
        }
      } else {
        fp.set('minDate', 'today');
      }
    }
  }, [date]);

  // Update state when initialValues change
  useEffect(() => {
    if (initialValues) {
      if (initialValues.departure) setDeparture(initialValues.departure);
      if (initialValues.arrival) setArrival(initialValues.arrival);
      if (initialValues.departure_name) setDepartureName(initialValues.departure_name);
      if (initialValues.arrival_name) setArrivalName(initialValues.arrival_name);
      if (initialValues.date) setDate(initialValues.date);
      if (initialValues.return_date) setReturnDate(initialValues.return_date);
      if (initialValues.flight_type) setFlightType(initialValues.flight_type);
      if (initialValues.flight_class) setFlightClass(initialValues.flight_class);
    }
  }, [initialValues]);

  // Auto-dismiss error message after 5 seconds
  useEffect(() => {
    if (toast && error) {
      const timer = setTimeout(() => {
        showToast(false);
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toast, error]);

  // Function to swap departure and arrival
  const swapAirports = () => {
    const tempDeparture = departure;
    const tempDepartureName = departure_name;
    setDeparture(arrival);
    setDepartureName(arrival_name);
    setArrival(tempDeparture);
    setArrivalName(tempDepartureName);
  };

  // Handle mobile date selection
  const handleMobileDateClick = (type) => {
    if (isMobile) {
      setCalendarStep(type);
      setShowMobileModal(true);
    }
  };

  // Helper function to format date as YYYY-MM-DD without timezone conversion
  const formatDateForStorage = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleMobileDateSelect = (selectedDate) => {
    if (calendarStep === 'departure') {
      setDate(formatDateForStorage(selectedDate));
      if (flight_type === 'return') {
        setCalendarStep('return');
      } else {
        setShowMobileModal(false);
      }
    } else if (calendarStep === 'return') {
      setReturnDate(formatDateForStorage(selectedDate));
      setShowMobileModal(false);
      setCalendarStep('departure');
    }
  };

  const closeMobileModal = () => {
    setShowMobileModal(false);
    setCalendarStep('departure');
  };

  // Handle city modal open
  const handleCityModalOpen = (type) => {
    setCityModalType(type);
    setCitySearchQuery('');
    setCitySearchResults([]);
    setShowCityModal(true);
  };

  // Handle city search
  useEffect(() => {
    if (!citySearchQuery || citySearchQuery.length < 3) {
      setCitySearchResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setCitySearchLoading(true);
      try {
        const response = await service.postAirportSearch({ keyword: citySearchQuery });
        const raw = response.data.data || [];
        const data = filterSanctionedAirports(raw);
        const options = data.map(s => {
          const code = s.iata_code ? s.iata_code : (s.address?.iata_city_code || '');
          // Use city_name if available, otherwise try to extract from name
          let cityName = s.city_name;
          
          // If city_name is not available, try to extract city from airport name
          if (!cityName && s.name) {
            // Check if name contains a comma (e.g., "Heathrow Airport, London")
            const nameParts = s.name.split(',');
            if (nameParts.length > 1) {
              // City is usually after the comma
              cityName = nameParts[nameParts.length - 1].trim();
            } else {
              // Try to extract city from airport name (e.g., "London Heathrow Airport" -> "London")
              // Remove common airport suffixes and take first word or words before airport name
              const airportName = s.name
                .replace(/\s*(Airport|International Airport|Intl Airport|Airfield)\s*$/i, '')
                .trim();
              
              // If it's a multi-word name, try to identify the city part
              // Common pattern: "City Airport Name" or "Airport Name City"
              const words = airportName.split(/\s+/);
              if (words.length > 2) {
                // Usually city is the first word(s) before the airport name
                // For "London Heathrow", city is "London"
                // For "John F. Kennedy International", city might be in address
                cityName = words[0]; // Take first word as city
              } else {
                cityName = airportName;
              }
            }
          }
          
          // Fallback to full name if we couldn't extract city
          if (!cityName) {
            cityName = s.name;
          }
          
          return {
            label: `${cityName} (${code})`,
            value: code,
            fullName: s.name // Keep full name for reference
          };
        });
        // Put Lahore, Karachi, Islamabad at end of list
        const pkPriority = ['LHE', 'KHI', 'ISB'];
        const sorted = [...options].sort((a, b) => {
          const ia = pkPriority.indexOf(a.value);
          const ib = pkPriority.indexOf(b.value);
          if (ia === -1 && ib === -1) return 0;
          if (ia === -1) return -1;
          if (ib === -1) return 1;
          return ia - ib;
        });
        setCitySearchResults(sorted);
      } catch (error) {
        console.error('Error searching airports:', error);
        setCitySearchResults([]);
      } finally {
        setCitySearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [citySearchQuery]);

  // Handle city selection
  const handleCitySelect = (city) => {
    if (cityModalType === 'departure') {
      setDeparture(city.value);
      setDepartureName(city.label);
    } else {
      setArrival(city.value);
      setArrivalName(city.label);
    }
    setShowCityModal(false);
    setCitySearchQuery('');
    setCitySearchResults([]);
  };

  const closeCityModal = () => {
    setShowCityModal(false);
    setCitySearchQuery('');
    setCitySearchResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any previous errors
    showToast(false);
    setError('');
    
    if (!departure || !arrival || !date) {
      setError('Please fill all required fields');
      showToast(true);
      return;
    }

    if (flight_type === 'return' && !return_date) {
      setError('Please select return date');
      showToast(true);
      return;
    }

    if (!phone || phone.trim() === '') {
      setError('Phone number is required');
      showToast(true);
      return;
    }

    if (!isValidPhoneNumber(phone)) {
      setError('Please enter a valid phone number (minimum 10 digits)');
      showToast(true);
      return;
    }

    // Show loading state
    setSubmitLoading(true);

    const bookingData = {
      departure,
      arrival,
      departure_name,
      arrival_name,
      date,
      return_date: flight_type === 'return' ? return_date : '',
      name: name || '',
      phone: phone || '',
      email: email || '',
      adults: String(adults),
      children: String(children),
      infants: String(infants),
      flight_type,
      flight_class
    };

    // Store booking data in localStorage for FlightDetailDuffel component
    localStorage.setItem('booking', JSON.stringify(bookingData));
    
    // Clear any existing offers to force fresh search on flight details page
    localStorage.removeItem('offer');
    
    console.log('Booking data stored:', bookingData);
    
    // If embedded in overlay and onSearch callback is provided, use it instead of navigating
    if (embeddedInOverlay && onSearch) {
      console.log('Calling onSearch callback for embedded overlay...');
      setSubmitLoading(false);
      onSearch(bookingData);
      return;
    }
    
    console.log('Navigating immediately to flight details page...');
    
    try {
      // Save booking search to database (for admin panel) - do this in background
      const bookingSearchParams = {
        departure: departure,
        arrival: arrival,
        departure_name: departure_name,
        arrival_name: arrival_name,
        date: date,
        return_date: flight_type === 'return' ? return_date : '',
        full_name: name || 'Guest',
        phone: phone || '',
        email: email || '',
        adult: adults,
        children: children,
        infants: infants,
        status: 'SEARCH',
        reviews: '',
        description: `${flight_type === 'return' ? 'Round Trip' : 'One Way'} - ${flight_class}`,
        flight_type: flight_type,
        flight_class: flight_class
      };
      
      // Save booking in background (don't wait for it)
      service.addBooking(bookingSearchParams).then(bookingResponse => {
        console.log('Booking search saved to database:', bookingResponse.data);
        // Store the booking ID for later use
        if (bookingResponse.data && bookingResponse.data.insertId) {
          localStorage.setItem('bookingresult', JSON.stringify(bookingResponse.data));
        }
      }).catch(err => {
        console.error('Error saving booking to database:', err);
        // Don't block navigation if this fails
      });

      // Navigate immediately - let the flight details page handle API calls
      // This way the user sees the progress indicator right away
      navigate(`/flight-detail-search?package=0`);
      
    } catch (error) {
      console.error('Error during flight search setup:', error);
      // Navigate anyway - the flight details page will handle errors
      navigate(`/flight-detail-search?package=0`);
    }
  };

  // Format date for display (e.g., "Mon, 22 Dec")
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch (error) {
      return dateString;
    }
  };

  // Format passengers display
  const formatPassengersDisplay = () => {
    const parts = [];
    if (adults > 0) {
      parts.push(`${adults} Adult${adults > 1 ? 's' : ''}`);
    }
    if (children > 0) {
      parts.push(`${children} Child${children > 1 ? 'ren' : ''}`);
    }
    if (infants > 0) {
      parts.push(`${infants} Infant${infants > 1 ? 's' : ''}`);
    }
    const passengerText = parts.length > 0 ? parts.join(', ') : 'Select Passengers';
    const cabinClass = flight_class.charAt(0).toUpperCase() + flight_class.slice(1);
    return `${passengerText} • ${cabinClass}`;
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-2 md:px-4 pt-0 pb-6 md:py-6">
      {/* Toast Error Message - Fixed at top of screen */}
      {toast && error && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md mx-auto px-4">
          <div className="bg-secondary text-white p-4 rounded-lg shadow-lg border border-secondary text-sm font-medium">
            {error}
          </div>
        </div>
      )}
      
      {/* Mobile View - White Card Design */}
      <div className={`${
        embeddedInOverlay ? 'bg-transparent p-0 rounded-none shadow-none border-0' :
        callby === 'edit_search' ? 'bg-transparent rounded-2xl p-4 md:p-6' :
        'bg-white rounded-2xl p-4 md:p-6 shadow-lg'
      }`}>
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Trip Type Selection - Pill-shaped buttons */}
          <div className="flex items-center justify-center md:justify-start gap-2 mb-4">
            <button
              type="button"
              onClick={() => {
                setFlightType('one-way');
                setReturnDate('');
              }}
              className={`px-4 py-2 text-sm rounded-full font-semibold transition-all duration-300 border-2 ${
                flight_type === 'one-way'
                  ? 'bg-navy-100 text-navy-700 border-secondary shadow-sm'
                  : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
              }`}
            >
              One-way
            </button>
            <button
              type="button"
              onClick={() => setFlightType('return')}
              className={`px-4 py-2 text-sm rounded-full font-semibold transition-all duration-300 border-2 ${
                flight_type === 'return'
                  ? 'bg-navy-100 text-navy-700 border-secondary shadow-sm'
                  : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
              }`}
            >
              Round-trip
            </button>
          </div>

          {/* From and To Section - Mobile Layout */}
          <div className={`md:hidden space-y-4 pb-4 ${embeddedInOverlay ? '' : 'border-b border-gray-200'}`}>
            {/* From Section */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 pt-1">
                <PlaneTakeoff className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <label className="text-xs text-gray-500 block mb-1">
                  From
                </label>
                <div 
                  onClick={() => handleCityModalOpen('departure')}
                  className="text-base font-bold text-gray-900 cursor-pointer"
                >
                  {departure_name || 'Select City or Airport'}
                </div>
              </div>
            </div>

            {/* To Section with Swap Button */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 pt-1">
                <PlaneLanding className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <label className="text-xs text-gray-500 block mb-1">
                  To
                </label>
                <div 
                  onClick={() => handleCityModalOpen('arrival')}
                  className="text-base font-bold text-gray-900 cursor-pointer"
                >
                  {arrival_name || 'Select City or Airport'}
                </div>
              </div>
            </div>
          </div>

          {/* From and To Section - Desktop Layout */}
          <div className="hidden md:grid grid-cols-2 gap-3 items-end">
            {/* From Section */}
            <div>
              <label htmlFor="departure-airport-select-desktop" className={`text-sm font-semibold mb-1.5 flex items-center gap-1.5 ${callby === 'edit_search' ? 'text-gray-800' : 'text-gray-800'}`}>
                <PlaneTakeoff className="w-3.5 h-3.5" />
                <span>From</span>
              </label>
              <div className="relative w-full group">
                <div className="absolute inset-0 bg-white rounded-lg border-2 border-gray-200 group-hover:border-navy-400 transition-all duration-300"></div>
                <div className="relative z-10 p-2.5 h-[46px] flex items-center gap-2">
                  <div className="flex-shrink-0">
                    <div className="p-1 rounded bg-navy-50">
                      <PlaneTakeoff className="w-4 h-4 text-secondary" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0" id="departure-airport-select-desktop">
                    <Select
                      placeholder="City or Airport"
                      value={departure && departure_name ? { label: departure_name, value: departure } : null}
                      onChange={(e) => {
                        setDeparture(e.value)
                        setDepartureName(e.label)
                      }} />
                  </div>
                </div>
              </div>
            </div>


            {/* To Section */}
            <div>
              <label htmlFor="arrival-airport-select-desktop" className={`text-sm font-semibold mb-1.5 flex items-center gap-1.5 ${callby === 'edit_search' ? 'text-gray-800' : 'text-gray-800'}`}>
                <PlaneLanding className="w-3.5 h-3.5" />
                <span>To</span>
              </label>
              <div className="relative w-full group">
                <div className="absolute inset-0 bg-white rounded-lg border-2 border-gray-200 group-hover:border-navy-400 transition-all duration-300"></div>
                <div className="relative z-10 p-2.5 h-[46px] flex items-center gap-2">
                  <div className="flex-shrink-0">
                    <div className="p-1 rounded bg-navy-50">
                      <PlaneLanding className="w-4 h-4 text-secondary" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0" id="arrival-airport-select-desktop">
                    <Select 
                      placeholder="City or Airport"
                      value={arrival && arrival_name ? { label: arrival_name, value: arrival } : null}
                      onChange={(e) => {
                        setArrival(e.value)
                        setArrivalName(e.label)
                      }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dates - Mobile Layout - Side by Side */}
          <div className={`md:hidden pb-4 ${embeddedInOverlay ? '' : 'border-b border-gray-200'}`}>
            <div className="grid grid-cols-2 gap-4">
              {/* Departure Date */}
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 pt-1">
                  <Calendar className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <label className="text-xs text-gray-500 block mb-1">
                    Departure Date
                  </label>
                  <div
                    onClick={() => handleMobileDateClick('departure')}
                    className="text-base font-bold text-gray-900 cursor-pointer"
                  >
                    {date ? formatDateDisplay(date) : 'Select'}
                  </div>
                </div>
              </div>

              {/* Return Date - Only show for return trips */}
              {flight_type === 'return' ? (
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 pt-1">
                    <Calendar className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="text-xs text-gray-500 block mb-1">
                      Return Date
                    </label>
                    <div
                      onClick={() => date && handleMobileDateClick('return')}
                      className={`text-base font-bold ${
                        !date ? 'text-gray-400' : 'text-gray-900 cursor-pointer'
                      }`}
                    >
                      {return_date ? formatDateDisplay(return_date) : 'Select'}
                    </div>
                  </div>
                </div>
              ) : (
                <div></div>
              )}
            </div>
          </div>

          {/* Dates Row - Desktop Layout */}
          <div className="hidden md:grid grid-cols-2 gap-3">
            {/* Departure Date */}
            <div>
              <label className={`text-sm font-semibold mb-1.5 flex items-center gap-1.5 ${callby === 'edit_search' ? 'text-gray-800' : 'text-gray-800'}`}>
                <Calendar className="w-3.5 h-3.5" />
                Departure Date
              </label>
              <input
                ref={departureInputRef}
                placeholder="Select Departure Date"
                readOnly
                className="w-full h-[46px] bg-white border-2 border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 cursor-pointer hover:border-navy-400 transition-all duration-300 focus:outline-none focus:border-secondary focus:ring-2 focus:ring-navy-200"
              />
            </div>

            {/* Return Date */}
            <div>
              <label className={`text-sm font-semibold mb-1.5 flex items-center gap-1.5 ${callby === 'edit_search' ? 'text-gray-800' : 'text-gray-800'}`}>
                <Calendar className="w-3.5 h-3.5" />
                Return Date
              </label>
              <input
                ref={returnInputRef}
                placeholder="Select Return Date"
                readOnly
                disabled={flight_type === 'one-way' || !date}
                className={`w-full h-[46px] bg-white border-2 border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 transition-all duration-300 focus:outline-none focus:border-secondary focus:ring-2 focus:ring-navy-200 ${
                  flight_type === 'one-way' || !date
                    ? 'cursor-not-allowed opacity-50 bg-gray-100'
                    : 'cursor-pointer hover:border-navy-400'
                }`}
              />
            </div>
          </div>

          {/* Passengers & Cabin Class - same row on mobile and desktop; opens modal on click */}
          <div className={`flex items-start gap-3 pb-4 ${embeddedInOverlay ? '' : 'border-b border-gray-200'}`}>
            <div className="flex-shrink-0 pt-1">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <label className="text-xs text-gray-500 block mb-1">
                Passengers & Cabin Class
              </label>
              <div 
                onClick={() => setShowPassengersModal(true)}
                className="text-base font-bold text-gray-900 mt-1 cursor-pointer"
              >
                {formatPassengersDisplay()}
              </div>
            </div>
          </div>

          {/* Phone & Email Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Phone Number - Required */}
            <div>
              <label className="text-sm font-semibold mb-1.5 flex items-center gap-1.5 text-gray-800">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 8V5z" />
                </svg>
                Phone Number <span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="tel"
                placeholder="e.g. 07700 900000"
                value={phone}
                onChange={handlePhoneChange}
                required
                className="w-full h-[46px] bg-white border-2 border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 hover:border-navy-400 transition-all duration-300 focus:outline-none focus:border-secondary focus:ring-2 focus:ring-navy-200"
              />
            </div>

            {/* Email */}
            <div>
              <label className="text-sm font-semibold mb-1.5 flex items-center gap-1.5 text-gray-800">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email Address
              </label>
              <input
                type="email"
                placeholder="e.g. you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-[46px] bg-white border-2 border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 hover:border-navy-400 transition-all duration-300 focus:outline-none focus:border-secondary focus:ring-2 focus:ring-navy-200"
              />
            </div>
          </div>

          {/* Submit Button - Blue for all devices */}
          <button 
            type="submit"
            disabled={submitLoading}
            className="w-full bg-secondary hover:bg-navy-700 text-white font-bold py-4 md:py-3 rounded-xl md:rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed mt-4"
          >
            {submitLoading ? (
              <div className="flex items-center justify-center gap-2">
                <img src={Spinner} alt="loading" className="w-5 h-5" />
                <span className="text-base md:text-sm md:text-base">SEARCHING...</span>
              </div>
            ) : (
              <span className="text-base md:text-sm md:text-base">Search Flights</span>
            )}
          </button>

          {/* Call for Phone Only Deals Button - mobile only */}
          <button
            type="button"
            onClick={() => window.location.href = 'tel:02081949506'}
            className="md:hidden w-full bg-white border-2 border-secondary text-secondary hover:bg-navy-50 font-bold py-4 md:py-3 rounded-xl md:rounded-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.01] active:scale-[0.99] mt-3"
          >
            <span className="text-base md:text-sm md:text-base">Call for Phone Only Deals</span>
          </button>
        </form>
      </div>

      {/* Mobile Calendar Modal */}
      {showMobileModal && isMobile && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-end md:items-center justify-center">
          <div className="bg-white w-full h-[90vh] md:h-auto md:max-w-md md:rounded-lg flex flex-col animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-secondary text-white">
              <h3 className="text-lg font-semibold">
                {calendarStep === 'departure' ? 'Select Departure Date' : 'Select Return Date'}
              </h3>
              <button
                onClick={closeMobileModal}
                className="p-1 hover:bg-navy-700 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Calendar */}
            <VerticalCalendar
              selectedDate={calendarStep === 'departure' ? (date ? new Date(date) : null) : (return_date ? new Date(return_date) : null)}
              onSelectDate={handleMobileDateSelect}
              minDate={calendarStep === 'return' && date ? new Date(date) : new Date()}
              departureDate={date ? new Date(date) : null}
              returnDate={return_date ? new Date(return_date) : null}
              isSelectingReturn={calendarStep === 'return'}
            />
          </div>
        </div>
      )}

      {/* Passengers & Cabin Class Modal - mobile and desktop */}
      {showPassengersModal && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-end md:items-center justify-center">
          <div className="bg-white w-full h-auto md:max-w-md md:rounded-lg flex flex-col animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-secondary text-white">
              <h3 className="text-lg font-semibold">
                Passengers & Cabin Class
              </h3>
              <button
                onClick={() => setShowPassengersModal(false)}
                className="p-1 hover:bg-navy-700 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Passengers Selection */}
            <div className="p-4 space-y-4">
              {/* Adults */}
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-bold text-base">Adults</p>
                  <p className="text-sm text-gray-600">18+</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => adults > 1 && setAdults(adults - 1)}
                    disabled={adults <= 1}
                    className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold text-lg transition-colors"
                  >
                    -
                  </button>
                  <span className="w-12 text-center font-semibold text-lg">{adults}</span>
                  <button
                    type="button"
                    onClick={() => setAdults(adults + 1)}
                    className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center font-bold text-lg transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Children */}
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-bold text-base">Children</p>
                  <p className="text-sm text-gray-600">Under 18 and Above 2</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => children > 0 && setChildren(children - 1)}
                    disabled={children <= 0}
                    className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold text-lg transition-colors"
                  >
                    -
                  </button>
                  <span className="w-12 text-center font-semibold text-lg">{children}</span>
                  <button
                    type="button"
                    onClick={() => setChildren(children + 1)}
                    className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center font-bold text-lg transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Infants */}
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-bold text-base">Infants</p>
                  <p className="text-sm text-gray-600">Under 2</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => infants > 0 && setInfants(infants - 1)}
                    disabled={infants <= 0}
                    className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold text-lg transition-colors"
                  >
                    -
                  </button>
                  <span className="w-12 text-center font-semibold text-lg">{infants}</span>
                  <button
                    type="button"
                    onClick={() => setInfants(infants + 1)}
                    className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center font-bold text-lg transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Cabin Class Selection */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <p className="font-bold text-base mb-3">Cabin Class</p>
                <div className="space-y-2">
                  {['economy', 'premium economy', 'business', 'first'].map((cabin) => (
                    <label 
                      key={cabin}
                      className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="radio"
                        name="cabinClass"
                        value={cabin}
                        checked={flight_class === cabin}
                        onChange={(e) => setFlightClass(e.target.value)}
                        className="mr-3 w-4 h-4"
                      />
                      <span className="text-base capitalize">{cabin}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Done Button */}
              <button
                type="button"
                onClick={() => setShowPassengersModal(false)}
                className="w-full bg-secondary hover:bg-navy-700 text-white font-semibold py-3 rounded-lg transition-colors mt-4"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile City Selection Modal */}
      {showCityModal && isMobile && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-end md:items-center justify-center">
          <div className="bg-white w-full h-[90vh] md:h-auto md:max-w-md md:rounded-lg flex flex-col animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-secondary text-white">
              <h3 className="text-lg font-semibold">
                {cityModalType === 'departure' ? 'Select Departure City' : 'Select Arrival City'}
              </h3>
              <button
                onClick={closeCityModal}
                className="p-1 hover:bg-navy-700 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Search Input */}
            <div className="p-4 border-b border-gray-200">
              <input
                type="text"
                value={citySearchQuery}
                onChange={(e) => setCitySearchQuery(e.target.value)}
                placeholder="Type at least 3 characters to search..."
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-secondary text-base"
              />
            </div>

            {/* Search Results */}
            <div className="flex-1 overflow-y-auto p-4">
              {citySearchLoading ? (
                <div className="flex items-center justify-center py-8 gap-2">
                  <span
                    className="w-8 h-8 border-2 border-navy-200 border-t-secondary rounded-full animate-spin"
                    aria-hidden
                  />
                  <span className="text-gray-600">Searching...</span>
                </div>
              ) : citySearchQuery.length > 0 && citySearchQuery.length < 3 ? (
                <div className="text-center py-8 text-gray-500">
                  Type at least 3 characters to search
                </div>
              ) : citySearchResults.length === 0 && citySearchQuery.length >= 3 ? (
                <div className="text-center py-8 text-gray-500">
                  No airports found
                </div>
              ) : citySearchResults.length > 0 ? (
                <div className="space-y-2">
                  {citySearchResults.map((city, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleCitySelect(city)}
                      className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-navy-50 hover:border-navy-300 transition-colors"
                    >
                      <div className="font-semibold text-gray-900">{city.label}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">Popular Cities</p>
                  <div className="space-y-2">
                    {popularCities.map((city, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleCitySelect(city)}
                        className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-navy-50 hover:border-navy-300 transition-colors"
                      >
                        <div className="font-semibold text-gray-900">{city.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InputSection;
